import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../component-styles/ReportsShared.css';
import API_BASE from '../../utils/api.js';
import { exportRowsToExcel } from '../../utils/excelExport.js';


function formatCurrency(v) {
  if (v == null || Number.isNaN(Number(v))) return '$0.00';
  return Number(v).toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}

function formatDate(v) {
  if (!v) return '-';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString();
}

function normalizeProject(raw) {
  return {
    id: String(raw.projectId || raw.id || raw.name || ''),
    name: raw.projectNickname || raw.name || raw.title || 'Untitled Project',
  };
}

export default function ItemTrackerReportPage() {
  const navigate = useNavigate();
  const userId = localStorage.getItem('userId');

  const [projects, setProjects] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [projectFilter, setProjectFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');

  useEffect(() => {
    async function loadProjects() {
      if (!userId) {
        setProjects([]);
        return;
      }

      try {
        const res = await fetch(`${API_BASE}/api/projects/${encodeURIComponent(userId)}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const arr = Array.isArray(data) ? data : data?.Items || data?.items || [];
        setProjects(arr.map(normalizeProject));
      } catch (err) {
        console.error('Failed to load projects', err);
        setProjects([]);
      }
    }

    loadProjects();
  }, [userId]);

  useEffect(() => {
    async function loadLibrary() {
      if (!userId) {
        setItems([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const res = await fetch(`${API_BASE}/api/library/${encodeURIComponent(userId)}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const list = Array.isArray(data?.items) ? data.items : [];
        setItems(list);
      } catch (err) {
        console.error('Failed to load item tracker data', err);
        setItems([]);
      } finally {
        setLoading(false);
      }
    }

    loadLibrary();
  }, [userId]);

  const projectMap = useMemo(() => {
    return projects.reduce((acc, p) => {
      acc[String(p.id)] = p.name;
      return acc;
    }, {});
  }, [projects]);

  const sources = useMemo(() => {
    const set = new Set();
    items.forEach((item) => {
      if (item.source) set.add(item.source);
    });
    return Array.from(set).sort();
  }, [items]);

  const filteredRows = useMemo(() => {
    return items.filter((item) => {
      if (projectFilter !== 'all' && String(item.projectId || '') !== projectFilter) return false;
      if (sourceFilter !== 'all' && (item.source || 'manual') !== sourceFilter) return false;
      return true;
    });
  }, [items, projectFilter, sourceFilter]);

  const totals = useMemo(() => {
    return filteredRows.reduce(
      (acc, item) => {
        acc.qty += Number(item.qty || 0);
        acc.total += Number(item.lineTotal || item.lineMerch || 0);
        return acc;
      },
      { qty: 0, total: 0 }
    );
  }, [filteredRows]);

  function exportExcel() {
    const headers = ['Item', 'Project', 'Room', 'Source', 'Qty', 'Client Unit', 'Line Total', 'Updated'];
    const rowsForExport = filteredRows.map((item) => [
      item.name || '',
      projectMap[String(item.projectId || '')] || 'Untitled Project',
      item.room || 'Unassigned',
      item.source || 'manual',
      Number(item.qty || 0),
      Number(item.clientUnit || 0),
      Number(item.lineTotal || item.lineMerch || 0),
      item.updatedAt || item.createdAt ? String(item.updatedAt || item.createdAt).slice(0, 10) : '',
    ]);

    exportRowsToExcel({
      filename: 'item-tracker-report.xlsx',
      sheetName: 'Item Tracker',
      headers,
      rows: rowsForExport,
    });
  }

  return (
    <div className="report-shell">
      <div className="report-shell-inner">
        <div className="report-header">
          <div className="report-title">
            <h1>Item Tracker</h1>
            <div className="muted">Active library items across projects</div>
          </div>
          <div className="report-actions">
            <button className="report-btn-secondary" onClick={() => navigate('/reports')}>
              Back
            </button>
            <button className="report-btn-primary" onClick={exportExcel}>
              Export Excel
            </button>
          </div>
        </div>

        <div className="report-filters">
          <select
            className="report-select"
            value={projectFilter}
            onChange={(e) => setProjectFilter(e.target.value)}
          >
            <option value="all">All Projects</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>

          <select
            className="report-select"
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
          >
            <option value="all">All Sources</option>
            {sources.map((source) => (
              <option key={source} value={source}>
                {source}
              </option>
            ))}
          </select>
        </div>

        <div className="report-card">
          <div className="report-metrics">
            <div className="report-metric">
              <div className="report-metric-value">{totals.qty}</div>
              <div className="report-metric-label">Total Quantity</div>
            </div>
            <div className="report-metric">
              <div className="report-metric-value">{formatCurrency(totals.total)}</div>
              <div className="report-metric-label">Tracked Value</div>
            </div>
          </div>
        </div>

        <div className="report-table-wrap">
          {loading ? (
            <div className="report-empty">Loading item tracker...</div>
          ) : filteredRows.length === 0 ? (
            <div className="report-empty">No items found.</div>
          ) : (
            <table className="report-table" role="table" aria-label="Item tracker">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Project</th>
                  <th>Room</th>
                  <th>Source</th>
                  <th className="right">Qty</th>
                  <th className="right">Client Unit</th>
                  <th className="right">Line Total</th>
                  <th>Updated</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((item) => (
                  <tr key={item.sk || item.id || item.name}>
                    <td>{item.name}</td>
                    <td>{projectMap[String(item.projectId || '')] || 'Untitled Project'}</td>
                    <td>{item.room || 'Unassigned'}</td>
                    <td>{item.source || 'manual'}</td>
                    <td className="right">{Number(item.qty || 0)}</td>
                    <td className="right">{formatCurrency(item.clientUnit || 0)}</td>
                    <td className="right">
                      {formatCurrency(item.lineTotal || item.lineMerch || 0)}
                    </td>
                    <td>{formatDate(item.updatedAt || item.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
